package com.merkost.handpass.admin.container

import com.merkost.handpass.admin.api.AdminRepositoryApi
import com.merkost.handpass.admin.contract.AsyncSection
import com.merkost.handpass.admin.contract.ListingDetailAction
import com.merkost.handpass.admin.contract.ListingDetailActionKind
import com.merkost.handpass.admin.contract.ListingDetailIntent
import com.merkost.handpass.admin.contract.ListingDetailState
import com.merkost.handpass.admin.contract.ListingDetailTab
import com.merkost.handpass.admin.util.toAdminMessage
import com.merkost.handpass.api.dto.admin.AdminConversationSummary
import kotlinx.coroutines.MainScope
import pro.respawn.flowmvi.api.Container
import pro.respawn.flowmvi.api.PipelineContext
import pro.respawn.flowmvi.dsl.lazyStore
import pro.respawn.flowmvi.plugins.recover
import pro.respawn.flowmvi.plugins.reduce

class ListingDetailContainer(private val repo: AdminRepositoryApi) : Container<ListingDetailState, ListingDetailIntent, ListingDetailAction> {

    private val scope = MainScope()

    private var loadedListingId: String? = null

    override val store by lazyStore(ListingDetailState.Loading, scope) {
        configure { name = "ListingDetailStore" }
        recover { e ->
            updateState { ListingDetailState.Error(e.toAdminMessage()) }
            null
        }
        reduce { intent ->
            when (intent) {
                is ListingDetailIntent.Init -> {
                    if (loadedListingId != intent.listingId) {
                        loadedListingId = intent.listingId
                        load(intent.listingId)
                        loadNotesAndAudit(intent.listingId)
                        runCatching { repo.recordView("listing", intent.listingId) }
                    }
                }
                is ListingDetailIntent.SetTab -> {
                    updateState {
                        if (this is ListingDetailState.Content) copy(activeTab = intent.tab) else this
                    }
                    if (intent.tab == ListingDetailTab.Conversations) {
                        val s = currentState() as? ListingDetailState.Content
                        if (s != null && !s.conversationsRequested) loadConversations()
                    }
                }
                ListingDetailIntent.TapSuspend -> emitOpenReasonModal(ListingDetailActionKind.Suspend)
                ListingDetailIntent.TapRestore -> emitOpenReasonModal(ListingDetailActionKind.Restore)
                is ListingDetailIntent.ConfirmAction -> runConfirmedAction(intent.kind, intent.reason)
                is ListingDetailIntent.UndoRestore -> undoRestore(intent.listingId)
                ListingDetailIntent.Refresh -> loadedListingId?.let { load(it) }
                is ListingDetailIntent.SetNoteDraft -> updateState {
                    if (this is ListingDetailState.Content) copy(newNoteDraft = intent.body) else this
                }
                is ListingDetailIntent.SubmitNote -> {
                    val s = currentState() as? ListingDetailState.Content ?: return@reduce
                    val listingId = loadedListingId ?: return@reduce
                    repo.createNote("listing", listingId, s.newNoteDraft).onSuccess {
                        action(ListingDetailAction.ShowSuccess("Note added"))
                        loadNotesAndAudit(listingId)
                        updateState { if (this is ListingDetailState.Content) copy(newNoteDraft = "") else this }
                    }.onFailure { e -> action(ListingDetailAction.ShowError(e.toAdminMessage())) }
                }
                is ListingDetailIntent.DeleteNote -> {
                    val listingId = loadedListingId ?: return@reduce
                    repo.deleteNote(intent.noteId).onSuccess {
                        action(ListingDetailAction.ShowSuccess("Note deleted"))
                        loadNotesAndAudit(listingId)
                    }.onFailure { e -> action(ListingDetailAction.ShowError(e.toAdminMessage())) }
                }
                ListingDetailIntent.LoadConversations -> loadConversations()
                is ListingDetailIntent.OpenConversation -> loadMessages(intent.conversation)
                ListingDetailIntent.CloseConversation -> updateState {
                    if (this is ListingDetailState.Content) copy(openConversation = null, messages = AsyncSection.Loading) else this
                }
            }
        }
    }

    private suspend fun PipelineContext<ListingDetailState, ListingDetailIntent, ListingDetailAction>.loadConversations() {
        val listingId = loadedListingId ?: return
        updateState {
            if (this is ListingDetailState.Content) copy(conversations = AsyncSection.Loading, conversationsRequested = true) else this
        }
        val convos = repo.getListingConversations(listingId)?.conversations
        updateState {
            if (this is ListingDetailState.Content) copy(
                conversations = if (convos != null) AsyncSection.Loaded(convos) else AsyncSection.Failed("Failed to load conversations"),
            ) else this
        }
    }

    private suspend fun PipelineContext<ListingDetailState, ListingDetailIntent, ListingDetailAction>.loadMessages(conversation: AdminConversationSummary) {
        updateState {
            if (this is ListingDetailState.Content) copy(openConversation = conversation, messages = AsyncSection.Loading) else this
        }
        val msgs = repo.getConversationMessages(conversation.id)?.messages
        updateState {
            if (this is ListingDetailState.Content) copy(
                messages = if (msgs != null) AsyncSection.Loaded(msgs) else AsyncSection.Failed("Failed to load messages"),
            ) else this
        }
    }

    private suspend fun PipelineContext<ListingDetailState, ListingDetailIntent, ListingDetailAction>.loadNotesAndAudit(listingId: String) {
        val notes = repo.listNotes("listing", listingId)?.notes
        val audit = repo.listAuditEntries("listing", listingId)?.entries
        updateState {
            if (this is ListingDetailState.Content) copy(
                notes = if (notes != null) AsyncSection.Loaded(notes) else AsyncSection.Failed("Failed to load notes"),
                audit = if (audit != null) AsyncSection.Loaded(audit) else AsyncSection.Failed("Failed to load audit"),
            ) else this
        }
    }

    private suspend fun PipelineContext<ListingDetailState, ListingDetailIntent, ListingDetailAction>.currentState(): ListingDetailState {
        var s: ListingDetailState = ListingDetailState.Loading
        withState { s = this }
        return s
    }

    private suspend fun PipelineContext<ListingDetailState, ListingDetailIntent, ListingDetailAction>.load(listingId: String) {
        updateState { ListingDetailState.Loading }
        val detail = repo.getListingDetail(listingId)
        if (detail == null) {
            updateState { ListingDetailState.Error("Listing not found") }
        } else {
            updateState { ListingDetailState.Content(detail) }
        }
    }

    private suspend fun PipelineContext<ListingDetailState, ListingDetailIntent, ListingDetailAction>.emitOpenReasonModal(kind: ListingDetailActionKind) {
        val s = currentState()
        val targetTitle = (s as? ListingDetailState.Content)?.detail?.title ?: "this listing"
        action(ListingDetailAction.OpenReasonModal(kind, targetTitle))
    }

    private suspend fun PipelineContext<ListingDetailState, ListingDetailIntent, ListingDetailAction>.runConfirmedAction(kind: ListingDetailActionKind, reason: String?) {
        val listingId = loadedListingId ?: return
        val result = when (kind) {
            ListingDetailActionKind.Suspend -> repo.suspendListing(listingId, reason)
            ListingDetailActionKind.Restore -> repo.restoreListing(listingId, reason)
        }
        result.onSuccess {
            when (kind) {
                ListingDetailActionKind.Suspend -> action(ListingDetailAction.ShowUndoableSuccess("Listing suspended", listingId))
                ListingDetailActionKind.Restore -> action(ListingDetailAction.ShowSuccess("Listing restored"))
            }
            load(listingId)
        }
        result.onFailure { e ->
            action(ListingDetailAction.ShowError(e.toAdminMessage()))
        }
    }

    private suspend fun PipelineContext<ListingDetailState, ListingDetailIntent, ListingDetailAction>.undoRestore(listingId: String) {
        repo.restoreListing(listingId, "Undo of suspension")
            .onSuccess {
                action(ListingDetailAction.ShowSuccess("Listing restored"))
                if (loadedListingId == listingId) load(listingId)
            }
            .onFailure { e -> action(ListingDetailAction.ShowError(e.toAdminMessage())) }
    }
}
